/* ConnectedCallView — Scan2Call fullscreen "in-call" UI for the resident.
   ---------------------------------------------------------------------
   Full two-way video call (restored Jul 2026 — see useResidentCall.js
   header). Layout: visitor's face fills the viewport; resident's own
   self-preview PIP (with flip-camera) docks bottom-right; controls dock
   at the bottom.
*/
import { PhoneOff, Mic, MicOff, Clock, Dog, Siren, Megaphone, ChevronDown, Wallet, AlertTriangle, MapPin, MessageSquare, ExternalLink, Video, VideoOff, SwitchCamera, NotebookPen } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { useCallChat } from "../../hooks/useCallChat";
import { visitorMapsUrl } from "./visitorMapsUrl";
import NetworkBars from "./NetworkBars";
import CallChatPanel from "./CallChatPanel";
import CallNotesDialog from "./CallNotesDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { DETERRENTS } from "./playDeterrent";

// Map a deterrent's icon name → lucide component. Kept here (not in
// playDeterrent.js) so the synth module stays React-free.
const ICONS = { Dog, Siren, Megaphone };

export default function ConnectedCallView({
  // ring payload
  ring,
  // call state
  elapsed, muted, videoOff, hasCamera, facingMode, barking, netQuality,
  deterrentId, selectedDeterrent,
  attachRemoteContainer, attachLocalContainer,
  // wallet state
  hasWallet, isLowFunds, fundedMm, fundedSs, nextDebitSs, pricePerMinNgn,
  // action callbacks
  onToggleMute, onToggleVideo, onFlipCamera, onBark, onPickDeterrent, onHangup,
}) {
  const SelectedIcon = ICONS[selectedDeterrent.iconName] || Dog;
  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const chat = useCallChat({ ringId: ring.ring_id, mySender: "resident", active: true });
  const mapsUrl = visitorMapsUrl(ring);
  // Call notes are a QR-branding (corporate) perk — probe once per call.
  const [notesAllowed, setNotesAllowed] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/doorbell/display-name`, { withCredentials: true });
        setNotesAllowed(!!r.data.allowed);
      } catch { /* keep hidden */ }
    })();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black text-white flex flex-col"
      style={{ height: "100dvh" }}
      data-testid="doorbell-ring-dialog"
    >
      {/* Visitor video fills the viewport. object-cover trims to
          crop-on-fill so tall phones don't get black bars.
          `attachRemoteContainer` is Agora's play target. */}
      <div
        ref={attachRemoteContainer}
        className="relative flex-1 w-full bg-black overflow-hidden [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
        data-testid="ring-visitor-video"
      >
        {/* Top overlay bar — visitor name + location + quality + timer.
            z-10 so the Agora-injected remote player (appended later in
            DOM order) can never paint over it. */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-start justify-between p-3 pointer-events-none">
          <div className="min-w-0 max-w-[65%]">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 font-body">
                Scan2Call
              </span>
            </div>
            {ring.visitor_name && (
              <p className="mt-1.5 px-2 py-0.5 rounded-md bg-black/50 backdrop-blur-sm font-heading text-base font-black tracking-tighter inline-block truncate max-w-full" data-testid="ring-visitor-name">
                {ring.visitor_name}
              </p>
            )}
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 flex items-center gap-1 text-[10px] text-gray-200 font-body max-w-full pointer-events-auto hover:text-emerald-300 transition-colors"
                title="Open visitor's location on Google Maps"
                data-testid="ring-visitor-location"
              >
                <MapPin className="w-3 h-3 shrink-0 text-emerald-300" />
                <span className="truncate px-1.5 py-0.5 rounded bg-black/50 backdrop-blur-sm underline decoration-dotted underline-offset-2">
                  {ring.visitor_location || "View visitor location"}
                </span>
                <ExternalLink className="w-2.5 h-2.5 shrink-0 opacity-70" />
              </a>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <NetworkBars quality={netQuality} testId="ring-net-quality" />
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500 text-black text-xs font-bold font-body">
              <Clock className="w-3 h-3" />
              <span className="font-mono" data-testid="ring-countdown">{mm}:{ss}</span>
            </div>
          </div>
        </div>

        {/* Self-preview PIP — resident's own camera, mirrored when the
            front camera is active. Flip button docks on the PIP corner. */}
        {hasCamera && (
          <div
            className="absolute right-3 bottom-16 z-10 w-24 h-36 sm:w-28 sm:h-40 rounded-lg overflow-hidden border border-white/25 shadow-xl bg-gray-900"
            data-testid="ring-self-pip"
          >
            <div
              ref={attachLocalContainer}
              className={`w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover ${
                facingMode === "user" ? "[&_video]:-scale-x-100" : ""
              }`}
            />
            {videoOff && (
              <div className="absolute inset-0 bg-gray-900/90 flex items-center justify-center" data-testid="ring-self-video-off">
                <VideoOff className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <button
              type="button"
              onClick={onFlipCamera}
              className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/25 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              title="Switch camera"
              aria-label="Switch camera"
              data-testid="ring-flip-camera-btn"
            >
              <SwitchCamera className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Funds banner — floats above the control bar so the resident
            can see time-remaining without eating the video area. */}
        {hasWallet && (
          <div
            className={`absolute left-3 right-3 bottom-3 z-10 flex items-center justify-between px-3 py-2 rounded-full backdrop-blur-md border ${
              isLowFunds
                ? "bg-amber-500/20 border-amber-400/50 text-amber-100"
                : "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
            }`}
            data-testid="ring-funds-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              {isLowFunds ? (
                <AlertTriangle className="w-4 h-4 shrink-0 animate-pulse" />
              ) : (
                <Wallet className="w-4 h-4 shrink-0" />
              )}
              <p className="font-mono text-sm font-bold tracking-tight" data-testid="ring-time-remaining">
                {fundedMm}:{fundedSs} left
              </p>
            </div>
            <p className="text-[10px] font-body opacity-80" data-testid="ring-next-debit">
              Next ₦{pricePerMinNgn} in 0:{nextDebitSs}
            </p>
          </div>
        )}
      </div>

      {/* Control bar — mute, video on/off, deterrent, chat, hangup. */}
      <div
        className="w-full flex items-center justify-center gap-3 bg-black/95 border-t border-white/10 px-4 pt-4"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
      >
        <button
          type="button"
          onClick={onToggleMute}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors border ${
            muted
              ? "bg-red-500/25 border-red-400/60 text-red-200"
              : "bg-white/10 border-white/20 text-white hover:bg-white/20"
          }`}
          title={muted ? "Unmute microphone" : "Mute microphone"}
          data-testid="ring-mute-btn"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        {hasCamera && (
          <button
            type="button"
            onClick={onToggleVideo}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors border ${
              videoOff
                ? "bg-red-500/25 border-red-400/60 text-red-200"
                : "bg-white/10 border-white/20 text-white hover:bg-white/20"
            }`}
            title={videoOff ? "Turn camera on" : "Turn camera off"}
            data-testid="ring-video-btn"
            aria-label={videoOff ? "Camera on" : "Camera off"}
          >
            {videoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          </button>
        )}

        {/* Deterrent — bark/siren/alarm. Wider pill so the short label
            stays legible at a glance. Dropdown chevron docks on the
            right for switching between deterrents. */}
        <div className="flex items-stretch gap-px rounded-full bg-white/10 border border-white/20 overflow-hidden">
          <button
            type="button"
            onClick={onBark}
            disabled={barking}
            className={`h-12 px-4 flex items-center gap-1.5 transition-colors font-body font-bold text-xs ${
              barking
                ? "bg-amber-500/25 text-amber-200 animate-pulse"
                : "text-white hover:bg-white/10"
            }`}
            title={selectedDeterrent.hint}
            data-testid="ring-bark-btn"
            aria-label={barking ? "Playing deterrent" : selectedDeterrent.label}
          >
            <SelectedIcon className="w-5 h-5 shrink-0" />
            <span className="uppercase tracking-wider">
              {barking ? "Playing" : selectedDeterrent.shortLabel}
            </span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={barking}
                className="h-12 px-2 flex items-center justify-center border-l border-white/15 text-white hover:bg-white/10"
                title="Choose deterrent"
                data-testid="ring-deterrent-picker"
                aria-label="Choose deterrent"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="ring-deterrent-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest font-bold font-body text-gray-500">
                Pick a deterrent
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {DETERRENTS.map((d) => {
                const Icon = ICONS[d.iconName] || Dog;
                const isSelected = d.id === deterrentId;
                return (
                  <DropdownMenuItem
                    key={d.id}
                    onSelect={() => onPickDeterrent(d.id)}
                    className={`gap-2 cursor-pointer ${isSelected ? "bg-emerald-50 text-emerald-900" : ""}`}
                    data-testid={`deterrent-option-${d.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0 text-gray-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold font-body leading-tight">{d.label}</p>
                      <p className="text-[10px] text-gray-500 font-body leading-tight truncate">{d.hint}</p>
                    </div>
                    {isSelected && <span className="text-emerald-600 text-xs font-bold font-body shrink-0">✓</span>}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <button
          type="button"
          onClick={() => chat.setOpen(true)}
          className="relative w-12 h-12 rounded-full flex items-center justify-center transition-colors border bg-white/10 border-white/20 text-white hover:bg-white/20"
          title="Chat with visitor"
          aria-label="Open chat"
          data-testid="ring-chat-btn"
        >
          <MessageSquare className="w-5 h-5" />
          {chat.unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold font-body flex items-center justify-center"
              data-testid="ring-chat-unread"
            >
              {chat.unread}
            </span>
          )}
        </button>

        {notesAllowed && (
          <button
            type="button"
            onClick={() => setNotesOpen(true)}
            className="w-12 h-12 rounded-full flex items-center justify-center transition-colors border bg-white/10 border-white/20 text-white hover:bg-white/20"
            title="Call notes"
            aria-label="Open call notes"
            data-testid="ring-notes-btn"
          >
            <NotebookPen className="w-5 h-5" />
          </button>
        )}

        <Button
          onClick={() => onHangup()}
          className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white p-0 shadow-lg"
          data-testid="ring-hangup-btn"
        >
          <PhoneOff className="w-6 h-6" />
        </Button>
      </div>

      <CallNotesDialog
        ringId={ring.ring_id}
        visitorName={ring.visitor_name}
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
      />
      <CallChatPanel
        open={chat.open}
        onClose={() => chat.setOpen(false)}
        messages={chat.messages}
        mySender="resident"
        onSend={chat.sendMessage}
        sending={chat.sending}
      />
    </div>
  );
}
